import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Dialog, DialogContent, DialogTitle } from '@mlm/ui';
import { labelizeLedgerKind } from '@mlm/shared';
import { getBinaryHistory, getBinaryPointsHistory, getMe } from '../../lib/api';
import { fmtNumber } from '../../lib/format';

/** "2026-06-30T…" → "Jun 30" — compact date for the points feed. */
function fmtDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Two-decimal number or em-dash for null. */
function num(v: string | null): string {
  return v == null ? '—' : Number(v).toFixed(2);
}

/**
 * Legacy-binary "Points History" — a compact feed of recent leg-PV contributions
 * (newest first). Clicking the card opens a dialog with the full admin-style
 * pivoted Binary History table. Gated by the caller on `binaryVariant` being
 * `'legacy'` or `'extreme'`.
 * Renders nothing until there's at least one row.
 */
export function BinaryPointsHistoryPanel({ slotId }: { slotId: string }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['binary-points-history', slotId],
    queryFn: () => getBinaryPointsHistory(slotId, 1, 10),
    enabled: !!slotId,
  });

  const rows = q.data?.rows ?? [];
  if (q.isLoading || rows.length === 0) return null;

  return (
    <>
      <Card
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="cursor-pointer transition-colors hover:bg-muted/40"
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
              Binary points history
            </p>
            <span className="text-caption font-medium text-primary">View full table →</span>
          </div>
          <div className="mt-3 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-caption text-muted-foreground">
                  <th className="pb-2 text-left font-normal">Date</th>
                  <th className="pb-2 text-left font-normal">Side</th>
                  <th className="pb-2 text-right font-normal">Points</th>
                  <th className="pb-2 text-right font-normal">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/60">
                    <td className="py-2 text-left text-muted-foreground">{fmtDay(r.createdAt)}</td>
                    <td className="py-2 text-left">
                      <span
                        className={
                          r.side === 'L'
                            ? 'rounded bg-primary/10 px-1.5 py-0.5 text-caption font-medium text-primary'
                            : 'rounded bg-amber-500/10 px-1.5 py-0.5 text-caption font-medium text-amber-600'
                        }
                      >
                        {r.side === 'L' ? 'Left' : 'Right'}
                      </span>
                    </td>
                    <td className="py-2 text-right tabular-nums">+{fmtNumber(r.delta)}</td>
                    <td className="py-2 text-right tabular-nums text-muted-foreground">
                      {r.balanceAfter != null ? fmtNumber(r.balanceAfter) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <BinaryHistoryDialog slotId={slotId} open={open} onOpenChange={setOpen} />
    </>
  );
}

/**
 * Full Binary History as the admin slot-detail shows it — one row per placement
 * event, both legs side by side, the pairing cash & GC, and flushed income.
 * Lazy-fetches when the dialog opens; horizontally scrollable on narrow screens.
 */
function BinaryHistoryDialog({
  slotId,
  open,
  onOpenChange,
}: {
  slotId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const meQ = useQuery({ queryKey: ['me'], queryFn: getMe });
  const histQ = useQuery({
    queryKey: ['binary-history', slotId],
    queryFn: () => getBinaryHistory(slotId),
    enabled: open && !!slotId,
  });
  const kindLabels = meQ.data?.ledgerLabels;
  const rows = histQ.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogTitle>Binary history</DialogTitle>
        <p className="-mt-1 text-xs text-muted-foreground">
          One row per placement event: leg PV before / added / after on each side, the pairing
          cash &amp; GC paid, and any flushed income (daily cash cap or max-pairs-per-day count
          cap).
        </p>
        <div className="overflow-x-auto">
          {histQ.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">No binary activity yet.</p>
          ) : (
            <table className="w-full text-sm whitespace-nowrap">
              <thead className="border-b text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">Log Date</th>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 text-right font-medium">Pairs</th>
                  <th className="px-3 py-2 text-right font-medium">Pair #</th>
                  <th className="px-3 py-2 font-medium">Cause ID</th>
                  <th className="px-3 py-2 text-right font-medium">Before (Left)</th>
                  <th className="px-3 py-2 text-right font-medium">Before (Right)</th>
                  <th className="px-3 py-2 text-right font-medium">Add (Left)</th>
                  <th className="px-3 py-2 text-right font-medium">Add (Right)</th>
                  <th className="px-3 py-2 text-right font-medium">After (Left)</th>
                  <th className="px-3 py-2 text-right font-medium">After (Right)</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-right font-medium">GC</th>
                  <th className="px-3 py-2 font-medium">Flushout</th>
                  <th className="px-3 py-2 text-right font-medium">Flushed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((h) => {
                  const typeLabel = h.kinds.length
                    ? h.kinds.map((k) => labelizeLedgerKind(k, kindLabels)).join(', ')
                    : '—';
                  return (
                    <tr key={h.sourceEventId} className="border-b last:border-0">
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {new Date(h.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-xs" title={h.kinds.join(', ')}>
                        {typeLabel}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {h.pairs > 0 ? h.pairs : '—'}
                      </td>
                      {/* Lifetime pair number(s) this event produced — lets a
                          member see which pair was their 5th, 10th, … and so
                          why it paid GC instead of cash. */}
                      <td className="px-3 py-2 text-right text-xs tabular-nums font-medium">
                        {h.pairs > 0
                          ? h.pairs === 1
                            ? `#${h.lifetimePairs}`
                            : `#${h.lifetimePairs - h.pairs + 1}–${h.lifetimePairs}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {h.causeSlotCode ? (
                          <span>
                            <span className="font-mono">{h.causeSlotCode}</span>
                            {h.causeOwnerName ? (
                              <span className="text-muted-foreground"> · {h.causeOwnerName}</span>
                            ) : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.beforeLeft)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.beforeRight)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">{num(h.addLeft)}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">{num(h.addRight)}</td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.afterLeft)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {num(h.afterRight)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums font-medium">
                        {Number(h.amount).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums">
                        {Number(h.gc).toFixed(2)}
                      </td>
                      <td className="px-3 py-2">
                        {h.flushout ? (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">
                            Yes
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">No</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs tabular-nums text-muted-foreground">
                        {Number(h.flushoutIncome) > 0 ? Number(h.flushoutIncome).toFixed(2) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
